import React, { useEffect, useState, useMemo } from "react";

// Material components
import { Box, Card, Grid, Typography, Button, IconButton, Collapse } from "@mui/material";

// Material Icons
import BoltIcon from "@mui/icons-material/Bolt";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";

// Custom Shared Components
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import BasicModal from "examples/modal/BasicModal/BasicModal";
import FDropdown from "components/Dropdown/FDropdown";
import DataTable from "examples/Tables/DataTable";
import FTextField from "components/Form/FTextField";
import pxToRem from "assets/theme/functions/pxToRem";

// Standard UI Components used across Lead Bay
import AddButton from "examples/NewDesign/CustomButton/AddButton";
import BasicButton from "examples/NewDesign/CustomButton/BasicButton";
import ResetFilterButton from "components/Buttons/ResetButton";
import CustomSearch from "components/Search/Search";

// Table Data
import BankOpeningData from "layouts/bankOpening/data/BankOpeningData";

// Redux
import { useDispatch, useSelector } from "react-redux";
import submitBankOpeningForm, { getAllBanksOpeningList } from "redux/Thunks/BankOpening";
import { openSnackbar } from "redux/Slice/Notification";
import { relaodBankOpeningList } from "redux/Slice/BankOpening";

// Constants
import Constants, {
  Icons,
  ButtonTitles,
  PageTitles,
  Colors,
  ModalContent,
  defaultData,
} from "utils/Constants";
import { paramCreater } from "utils/methods/methods";
import Validator from "utils/Validations";

const initialBankOpeningState = {
  type: "new",
  openModal: false,
  list: [],
  body: {
    beneficiary_name: "",
    beneficiary_email: "",
    beneficiary_mobile_no: "",
    beneficiary_pan: "",
    beneficiary_adhar: "",
    bank_opening_category: "",
    bank_opening_name: "",
  },
  errors: {},
  loading: false,
};

// SVG Bank Logos
function KotakLogo() {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {/* Kotak Infinity Icon */}
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          backgroundColor: "#ED1C24",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: Colors.WHITE,
          fontWeight: 900,
          fontSize: "16px",
        }}
      >
        <span style={{ transform: "rotate(-25deg)", display: "inline-block" }}>∞</span>
      </Box>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: pxToRem(18),
            color: "#ED1C24",
            letterSpacing: "-0.5px",
          }}
        >
          kotak
        </Typography>
      </Box>
      <Box
        sx={{
          borderLeft: "1px solid #D0D5DD",
          pl: 1,
          ml: 0.5,
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: pxToRem(20),
            color: "#0F172A",
            letterSpacing: "-0.5px",
            lineHeight: 1,
          }}
        >
          811
        </Typography>
      </Box>
    </Box>
  );
}

function EquitasLogo() {
  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", alignItems: "baseline" }}>
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: pxToRem(20),
            color: Colors.PRIMARY,
            letterSpacing: "-0.5px",
            lineHeight: 1,
          }}
        >
          equitas
        </Typography>
      </Box>
      <Box
        sx={{
          backgroundColor: "#ED1C24",
          color: Colors.WHITE,
          borderRadius: "2px",
          px: 0.5,
          py: 0.1,
          display: "inline-block",
          width: "fit-content",
          mt: 0.25,
        }}
      >
        <Typography
          sx={{
            fontSize: "9px",
            fontWeight: 700,
            color: Colors.WHITE,
            letterSpacing: "0.02em",
            lineHeight: 1.1,
          }}
        >
          Small Finance Bank
        </Typography>
      </Box>
    </Box>
  );
}

function IDFCLogo() {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: "6px",
          backgroundColor: "#9A0026",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: Colors.WHITE,
          flexShrink: 0,
        }}
      >
        <AccountBalanceIcon sx={{ fontSize: pxToRem(20), color: Colors.WHITE }} />
      </Box>
      <Box>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: pxToRem(14),
            color: "#9A0026",
            lineHeight: 1.1,
            letterSpacing: "-0.2px",
          }}
        >
          IDFC FIRST
        </Typography>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: pxToRem(11),
            color: "#0F172A",
            lineHeight: 1.1,
          }}
        >
          Bank
        </Typography>
      </Box>
    </Box>
  );
}

function BankOpening() {
  const [bankOpening, setBankOpening] = useState(initialBankOpeningState);
  const [loading, setLoading] = useState(false);
  const [expandedBenefits, setExpandedBenefits] = useState({
    "bank-kotak": true,
    "bank-equitas": true,
    "bank-idfc": true,
  });
  const [searchQuery, setSearchQuery] = useState("");

  const [tablePagination, setTablePagination] = useState({
    page: 0,
    perPage: defaultData.DATE_ON_SINGLE_API_CALL,
  });
  const [next, setNext] = useState(0);
  const bankList = useSelector((state) => state.bank);
  const dispatch = useDispatch();

  const handleCloseReportType = () =>
    setBankOpening(JSON.parse(JSON.stringify(initialBankOpeningState)));

  const { columns, rows } = BankOpeningData({
    bankOpeningSubmittedFormLoading: bankList.bankOpeningLoading,
    bankOpeningSubmittedForm: bankList.bankOpeningList,
  });

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows;
    const q = searchQuery.toLowerCase().trim();
    return rows.filter((r) => {
      const name = r.name?.props?.name || "";
      const email = r.email?.props?.name || "";
      const phone = r.phone?.props?.name || "";
      const pan = r.pan?.props?.name || "";
      const bank = r.bank?.props?.name || "";
      return (
        name.toLowerCase().includes(q) ||
        email.toLowerCase().includes(q) ||
        phone.toLowerCase().includes(q) ||
        pan.toLowerCase().includes(q) ||
        bank.toLowerCase().includes(q)
      );
    });
  }, [rows, searchQuery]);

  const handleFilter = async () => {
    setLoading(Constants.PENDING);
    setTablePagination({ ...tablePagination, page: 0 });
    setNext(0);
    const paramData = {
      page: 0,
      perPage: tablePagination.perPage,
    };
    const data = new URLSearchParams(paramData);
    await dispatch(getAllBanksOpeningList(data));
    setLoading(Constants.FULFILLED);
  };

  const handleResetFilter = () => {
    setSearchQuery("");
    handleFilter();
  };

  useEffect(() => {
    (async () => {
      handleFilter();
    })();
  }, []);

  const handleChange = async (e) => {
    const { name, value } = e.target;
    const temp = { ...bankOpening };
    temp.body[name] = value;
    setBankOpening(temp);
  };

  const bankFormValidations = () => {
    const {
      beneficiary_name: beneficiaryName,
      beneficiary_email: beneficiaryEmail,
      beneficiary_mobile_no: beneficiaryMobileNo,
      beneficiary_pan: beneficiaryPan,
      beneficiary_adhar: beneficiaryAdhar,
      bank_opening_category: bankOpeningCategory,
      bank_opening_name: bankOpeningName,
    } = bankOpening.body;
    const errors = {};

    const beneficiaryNameValidation = Validator.validate("basic2", beneficiaryName);
    const beneficiaryEmailValidation = Validator.validate("basic", beneficiaryEmail);
    const beneficiaryMobileNoValidation = Validator.validate("basic2", beneficiaryMobileNo);
    const beneficiarypanValidation = Validator.validate("basic2", beneficiaryPan);
    const beneficiaryadharValidation = Validator.validate("basic2", beneficiaryAdhar);
    const bankopeningcategoryValidation = Validator.validate("basic", bankOpeningCategory);
    const bankopeningNameValidation = Validator.validate("basic2", bankOpeningName);

    if (beneficiaryNameValidation !== "") errors.beneficiary_name = beneficiaryNameValidation;
    if (beneficiaryEmailValidation !== "") errors.beneficiary_email = beneficiaryEmailValidation;
    if (beneficiaryMobileNoValidation !== "")
      errors.beneficiary_mobile_no = beneficiaryMobileNoValidation;
    if (beneficiarypanValidation !== "") errors.beneficiary_pan = beneficiarypanValidation;
    if (beneficiaryadharValidation !== "") errors.beneficiary_adhar = beneficiaryadharValidation;
    if (bankopeningcategoryValidation !== "")
      errors.bank_opening_category = bankopeningcategoryValidation;
    if (bankopeningNameValidation !== "") errors.bank_opening_name = bankopeningNameValidation;

    return Object.keys(errors).length > 0 ? errors : {};
  };

  const handleSubmit = async () => {
    const errors = bankFormValidations();
    setBankOpening((prev) => ({ ...prev, errors, loading: true }));
    if (Object.keys(errors).length === 0) {
      const res = await dispatch(submitBankOpeningForm(bankOpening.body));
      if (res.payload.status === 200) {
        handleFilter();
        dispatch(
          openSnackbar({
            message: Constants.SETUP_REPORT_CREATE_SUCCESS,
            notificationType: Constants.NOTIFICATION_SUCCESS,
          })
        );
        handleCloseReportType();
      } else if (res.payload.status === 422) {
        setBankOpening((prev) => ({
          ...prev,
          errors: { title: Constants.SETUP_REPORT_CREATE_ERROR },
        }));
      }
    } else {
      setBankOpening((prev) => ({ ...prev, errors }));
    }
    setBankOpening((prev) => ({ ...prev, loading: false }));
  };

  const handleTablePagination = async () => {
    const paramData = {
      page: next + 1,
      perPage: tablePagination.perPage,
    };
    const res = await dispatch(
      getAllBanksOpeningList({ param: paramCreater(paramData), byPassSlice: false })
    );
    if (res.payload.status === 200) setNext(res.payload.data.data.length > 0 ? next + 1 : next);
  };

  const handleReload = async () => {
    await dispatch(relaodBankOpeningList());
    handleFilter();
  };

  // Open bank link in a new tab
  const handleOpenBankLink = (url) => {
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const toggleBenefit = (bankId) => {
    setExpandedBenefits((prev) => ({
      ...prev,
      [bankId]: !prev[bankId],
    }));
  };

  const bankCardsData = [
    {
      id: "bank-kotak",
      bankKey: Constants.KOTAK,
      url: "https://sales.gromo.in/kt/j1Kw_6xIbm",
      tabTitle: "Kotak 811",
      logo: <KotakLogo />,
      title: "Kotak 811 Savings Account",
      desc: "A modern digital savings account for today's lifestyle.",
      points: ["Fully online account opening", "Use Referral Code: TBA123"],
      minBalance: "ZERO",
      creationTime: "1 Day",
      benefits: [
        "Zero balance account with no annual maintenance charges",
        "Free instant Virtual Visa debit card for online shopping",
        "Earn up to 4.0% interest rate p.a. on your savings",
        "Seamless digital transfers via UPI, IMPS, and NEFT",
      ],
    },
    {
      id: "bank-equitas",
      bankKey: "Equitas",
      url: "https://sales.gromo.in/eq/j1Kw_6xIbm",
      tabTitle: "Equitas Selfie",
      logo: <EquitasLogo />,
      title: "Equitas Selfie Savings Account",
      desc: "Open your account in minutes, anytime anywhere.",
      points: ["Fully online account opening", "Video KYC for quick activation"],
      minBalance: "ZERO",
      creationTime: "1 Day",
      benefits: [
        "Earn attractive interest rates up to 7% p.a. on savings balance",
        "Zero balance requirement with zero non-maintenance penalties",
        "Free & unlimited online transfers across all banking networks",
        "Convenient Video KYC verification from the comfort of your home",
      ],
    },
    {
      id: "bank-idfc",
      bankKey: "IDFC FIRST",
      url: "https://sales.gromo.in/ia/j1Kw_6xIbm",
      tabTitle: "IDFC FIRST",
      logo: <IDFCLogo />,
      title: "IDFC FIRST Savings Account",
      desc: "A feature-rich account with great benefits.",
      points: ["Fully online account opening", "Instant account activation"],
      minBalance: "ZERO",
      creationTime: "1 Day",
      benefits: [
        "Monthly interest payouts on your savings account balance",
        "Unlimited free ATM transactions at any domestic bank ATM",
        "Zero-fee banking on 28+ essential services and features",
        "Complimentary purchase protection and personal accident coverage",
      ],
    },
  ];

  return (
    <DashboardLayout>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1, pb: 4 }}>
        {/* Top Header Card with Value Propositions and Right Graphic Banner */}
        <Card
          sx={{
            p: { xs: 2.5, sm: 3.5 },
            borderRadius: "16px",
            border: "1px solid #E2E8F0",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.04)",
            backgroundColor: Colors.WHITE,
          }}
        >
          <Grid container spacing={3} alignItems="center">
            {/* Left Header Content */}
            <Grid item xs={12} lg={7}>
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: pxToRem(22), sm: pxToRem(26) },
                  fontWeight: 800,
                  color: "#0F172A",
                  letterSpacing: "-0.5px",
                }}
              >
                {PageTitles.BANK_ACCOUNT_OPENING}
              </Typography>
              <Typography
                sx={{
                  fontSize: pxToRem(13.5),
                  color: "#64748B",
                }}
              >
                Open your bank account digitally with trusted banking partners.
              </Typography>
            </Grid>

            {/* Right Graphic Banner */}
            <Grid item xs={12} lg={5}>
              {/* 3 Value Propositions */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: { xs: 2, sm: 3 },
                }}
              >
                {/* 1 */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "10px",
                      backgroundColor: "#EFF6FF",
                      border: "1px solid #DBEAFE",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: Colors.PRIMARY,
                      flexShrink: 0,
                    }}
                  >
                    <BoltIcon sx={{ fontSize: pxToRem(20) }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: pxToRem(13), fontWeight: 700, color: "#101828" }}>
                      100% Digital Process
                    </Typography>
                    <Typography sx={{ fontSize: pxToRem(11.5), color: "#64748B" }}>
                      Quick & Paperless
                    </Typography>
                  </Box>
                </Box>

                {/* 2 */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "10px",
                      backgroundColor: "#EFF6FF",
                      border: "1px solid #DBEAFE",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: Colors.PRIMARY,
                      flexShrink: 0,
                    }}
                  >
                    <ShieldOutlinedIcon sx={{ fontSize: pxToRem(20) }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: pxToRem(13), fontWeight: 700, color: "#101828" }}>
                      Trusted Banks
                    </Typography>
                    <Typography sx={{ fontSize: pxToRem(11.5), color: "#64748B" }}>
                      Safe & Secure
                    </Typography>
                  </Box>
                </Box>

                {/* 3 */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "10px",
                      backgroundColor: "#EFF6FF",
                      border: "1px solid #DBEAFE",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: Colors.PRIMARY,
                      flexShrink: 0,
                    }}
                  >
                    <SupportAgentOutlinedIcon sx={{ fontSize: pxToRem(20) }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: pxToRem(13), fontWeight: 700, color: "#101828" }}>
                      Dedicated Support
                    </Typography>
                    <Typography sx={{ fontSize: pxToRem(11.5), color: "#64748B" }}>
                      We&apos;re here to help
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Card>

        {/* 3 Bank Cards Grid */}
        <Grid container spacing={3}>
          {bankCardsData.map((bank) => {
            const isBenefitExpanded = !!expandedBenefits[bank.id];

            return (
              <Grid item xs={12} md={4} key={bank.id}>
                <Card
                  onClick={() => handleOpenBankLink(bank.url)}
                  sx={{
                    p: { xs: 2.5, sm: 3 },
                    borderRadius: "16px",
                    backgroundColor: Colors.WHITE,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    cursor: "pointer",
                    transition: "all 0.25s ease-in-out",
                    "&:hover": {
                      borderColor: Colors.PRIMARY,
                      transform: "translateY(-2px)",
                      boxShadow: "0 10px 30px rgba(4, 59, 159, 0.12)",
                    },
                  }}
                >
                  {/* Card Header: Bank Logo + Arrow Button */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2.5,
                      minHeight: 44,
                    }}
                  >
                    {bank.logo}
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenBankLink(bank.url);
                      }}
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        backgroundColor: "#EFF6FF",
                        color: Colors.PRIMARY,
                        border: "1px solid #DBEAFE",
                        "&:hover": {
                          backgroundColor: "#DBEAFE",
                        },
                      }}
                    >
                      <ChevronRightIcon sx={{ fontSize: pxToRem(20) }} />
                    </IconButton>
                  </Box>

                  {/* Title & Description */}
                  <Typography
                    sx={{
                      fontSize: pxToRem(15.5),
                      fontWeight: 800,
                      color: "#0F172A",
                      lineHeight: 1.3,
                      mb: 0.75,
                    }}
                  >
                    {bank.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: pxToRem(12.5),
                      color: "#64748B",
                      lineHeight: 1.45,
                      mb: 2.5,
                      minHeight: "36px",
                    }}
                  >
                    {bank.desc}
                  </Typography>

                  {/* Checkpoints */}
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2, mb: 2.5 }}>
                    {bank.points.map((pt) => (
                      <Box key={pt} sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                        <CheckCircleIcon sx={{ fontSize: pxToRem(17), color: "#16A34A" }} />
                        <Typography
                          sx={{
                            fontSize: pxToRem(12.5),
                            color: "#334155",
                            fontWeight: 500,
                          }}
                        >
                          {pt}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* 2 Stat Boxes */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 1.5,
                      mb: 2.5,
                    }}
                  >
                    <Box
                      sx={{
                        p: 1.25,
                        borderRadius: "10px",
                        backgroundColor: "#F8FAFC",
                        border: "1px solid #EAECF0",
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        sx={{ fontSize: pxToRem(10.5), color: "#64748B", fontWeight: 500 }}
                      >
                        Min. Account Balance
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: pxToRem(13.5),
                          fontWeight: 800,
                          color: "#101828",
                          mt: 0.25,
                        }}
                      >
                        {bank.minBalance}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        p: 1.25,
                        borderRadius: "10px",
                        backgroundColor: "#F8FAFC",
                        border: "1px solid #EAECF0",
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        sx={{ fontSize: pxToRem(10.5), color: "#64748B", fontWeight: 500 }}
                      >
                        Account Creation Time
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: pxToRem(13.5),
                          fontWeight: 800,
                          color: "#101828",
                          mt: 0.25,
                        }}
                      >
                        {bank.creationTime}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Open Account Button */}
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenBankLink(bank.url);
                    }}
                    endIcon={<ArrowForwardIcon sx={{ fontSize: pxToRem(16) }} />}
                    sx={{
                      backgroundColor: Colors.PRIMARY,
                      color: Colors.WHITE,
                      textTransform: "none",
                      fontSize: pxToRem(13.5),
                      fontWeight: 700,
                      borderRadius: "8px",
                      py: 1.1,
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "#032d7b",
                        boxShadow: "0 4px 12px rgba(4, 59, 159, 0.25)",
                      },
                    }}
                  >
                    Open Account
                  </Button>

                  {/* Accordion: Benefits ⌵ */}
                  <Box
                    sx={{ mt: 2, pt: 1, borderTop: "1px solid #F1F5F9" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleBenefit(bank.id);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer",
                        py: 0.5,
                        color: "#344054",
                        "&:hover": {
                          color: Colors.PRIMARY,
                        },
                      }}
                    >
                      <Typography sx={{ fontSize: pxToRem(12.5), fontWeight: 600 }}>
                        Benefits
                      </Typography>
                      {isBenefitExpanded ? (
                        <KeyboardArrowUpIcon sx={{ fontSize: pxToRem(18), color: "#64748B" }} />
                      ) : (
                        <KeyboardArrowDownIcon sx={{ fontSize: pxToRem(18), color: "#64748B" }} />
                      )}
                    </Box>

                    <Collapse in={isBenefitExpanded}>
                      <Box
                        sx={{
                          mt: 1,
                          p: 1.5,
                          borderRadius: "8px",
                          backgroundColor: "#F8FAFC",
                          border: "1px solid #EAECF0",
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.75,
                        }}
                      >
                        {bank.benefits.map((b) => (
                          <Box key={b} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                            <Box
                              sx={{
                                width: 5,
                                height: 5,
                                borderRadius: "50%",
                                backgroundColor: Colors.PRIMARY,
                                mt: 0.8,
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              sx={{ fontSize: pxToRem(11.5), color: "#475467", lineHeight: 1.4 }}
                            >
                              {b}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Collapse>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Leads Table Management Section */}
        <Box sx={{ mt: 2 }}>
          {/* Section Header Controls */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
              mb: 2,
            }}
          >
            {/* Title & Subtitle */}
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontSize: pxToRem(18),
                  fontWeight: 800,
                  color: "#101828",
                  letterSpacing: "-0.3px",
                }}
              >
                Your Bank Account Leads
              </Typography>
              <Typography sx={{ fontSize: pxToRem(12.5), color: "#667085", mt: 0.25 }}>
                Track and manage all your bank account opening requests.
              </Typography>
            </Box>

            {/* Right Standard Action Controls: CustomSearch, AddButton, BasicButton (Filter & Reload), ResetFilterButton */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              {/* Common Search Component */}
              <CustomSearch
                options={[]}
                value={searchQuery}
                debouncedHandleSearch={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, mobile, bank etc."
                label=""
                width={pxToRem(260)}
                marginRight={0}
                freeSolos
              />

              {/* Standard AddButton */}
              <AddButton
                title={ButtonTitles.BANK_OPENING}
                icon={Icons.ADD}
                suffixIcon={Icons.BANK}
                background={Colors.WHITE}
                color={Colors.BLACK}
                action={() => setBankOpening((prev) => ({ ...prev, openModal: true }))}
                ml={pxToRem(0)}
                border
              />

              {/* Standard BasicButton for Filters */}
              <BasicButton
                title="Filters"
                icon="tune"
                background={Colors.WHITE}
                color={Colors.BLACK}
                action={handleFilter}
                ml={pxToRem(0)}
                border
              />

              {/* Standard BasicButton for Reload */}
              <BasicButton
                icon={Icons.RELOAD}
                background={Colors.WHITE}
                color={Colors.BLACK}
                action={handleReload}
                ml={pxToRem(0)}
                border
              />

              {/* Standard ResetFilterButton */}
              <ResetFilterButton handleReset={handleResetFilter} marginTop={0} />
            </Box>
          </Box>

          {/* Leads Table */}
          <Card
            sx={{
              borderRadius: "16px",
              border: "1px solid #E2E8F0",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.04)",
              overflow: "hidden",
            }}
          >
            <MDBox>
              <DataTable
                table={{ columns, rows: filteredRows }}
                isSorted={false}
                entriesPerPage={{ defaultValue: defaultData.PER_PAGE }}
                showTotalEntries={false}
                noEndBorder
                loading={bankList.bankOpeningLoading}
                currentPage={tablePagination.page}
                handleTablePagination={handleTablePagination}
                handleCurrentPage={(page) => setTablePagination({ ...tablePagination, page })}
              />
            </MDBox>
          </Card>
        </Box>
      </Box>

      {/* Create and Update Bank Account Modal */}
      <BasicModal
        open={bankOpening.openModal}
        handleClose={handleCloseReportType}
        title={
          bankOpening.type === "new"
            ? ModalContent.NEW_BANK_ACCOUNT
            : ModalContent.EDIT_BANK_ACCOUNT
        }
        actionButton={
          (!bankOpening.loading && ButtonTitles.SUBMIT) ||
          (bankOpening.loading && ButtonTitles.SUBMIT_LOADING)
        }
        disabled={loading === Constants.PENDING}
        handleAction={handleSubmit}
      >
        <FTextField
          label="Full Name*"
          name="beneficiary_name"
          id="beneficiary_name"
          placeholder="Enter Full Name"
          type="text"
          value={bankOpening.body.beneficiary_name}
          handleChange={handleChange}
          width="100%"
          error={bankOpening.errors?.beneficiary_name}
          helperText={bankOpening.errors?.beneficiary_name}
          marginBottom={pxToRem(10)}
        />

        <FTextField
          label="Email*"
          name="beneficiary_email"
          id="beneficiary_email"
          placeholder="Enter Email"
          type="email"
          value={bankOpening.body.beneficiary_email}
          handleChange={handleChange}
          width="100%"
          error={bankOpening.errors?.beneficiary_email}
          helperText={bankOpening.errors?.beneficiary_email}
          marginBottom={pxToRem(10)}
        />

        <FTextField
          label="Mobile Number*"
          name="beneficiary_mobile_no"
          id="beneficiary_mobile_no"
          placeholder="Enter Mobile "
          type="number"
          value={bankOpening.body.beneficiary_mobile_no}
          handleChange={handleChange}
          width="100%"
          error={bankOpening.errors?.beneficiary_mobile_no}
          helperText={bankOpening.errors?.beneficiary_mobile_no}
          marginBottom={pxToRem(10)}
        />

        <FTextField
          label="Pan*"
          name="beneficiary_pan"
          id="beneficiary_pan"
          placeholder="Enter pan number"
          type="text"
          value={bankOpening.body.beneficiary_pan}
          handleChange={handleChange}
          width="100%"
          error={bankOpening.errors?.beneficiary_pan}
          helperText={bankOpening.errors?.beneficiary_pan}
          marginBottom={pxToRem(10)}
        />

        <FTextField
          label="Aadhar*"
          name="beneficiary_adhar"
          id="beneficiary_adhar"
          placeholder="Enter Aadhar number"
          type="text"
          value={bankOpening.body.beneficiary_adhar}
          handleChange={handleChange}
          width="100%"
          error={bankOpening.errors?.beneficiary_adhar}
          helperText={bankOpening.errors?.beneficiary_adhar}
          marginBottom={pxToRem(10)}
        />

        <FDropdown
          label="Bank Category*"
          id="bank_opening_category"
          name="bank_opening_category"
          value={bankOpening.body.bank_opening_category}
          menu={[Constants.SAVING, Constants.CREDIT]}
          error={bankOpening.errors?.bank_opening_category}
          helperText={bankOpening.errors?.bank_opening_category}
          handleChange={(name, value, id) => handleChange({ target: { name, value, id } })}
          marginBottom={0}
          mr={0}
        />

        <FDropdown
          label="Bank*"
          id="bank_opening_name"
          name="bank_opening_name"
          value={bankOpening.body.bank_opening_name}
          menu={[Constants.KOTAK, Constants.AU, Constants.INDUSIND]}
          error={bankOpening.errors?.bank_opening_name}
          helperText={bankOpening.errors?.bank_opening_name}
          handleChange={(name, value, id) => handleChange({ target: { name, value, id } })}
          marginBottom={0}
          mr={0}
        />

        {bankOpening.loading && (
          <MDBox py={5} display="flex" justifyContent="center" alignItems="center">
            {Icons.LOADING2}
          </MDBox>
        )}
      </BasicModal>
    </DashboardLayout>
  );
}

export default BankOpening;
